import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, query } from 'firebase/firestore';
import { db, getSystemSettings } from '../services/firebase';
import { User, Team, SystemSettings } from '../types';

interface UseAppDataReturn {
  users: User[];
  teams: Team[];
  systemSettings: SystemSettings;
  usersLoaded: boolean;
  setUsers: React.Dispatch<React.SetStateAction<User[]>>;
  setSystemSettings: React.Dispatch<React.SetStateAction<SystemSettings>>;
}

export const useAppData = (): UseAppDataReturn => {
  const [users, setUsers] = useState<User[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [systemSettings, setSystemSettings] = useState<SystemSettings>({});
  const [usersLoaded, setUsersLoaded] = useState(false);

  // Users Listener
  useEffect(() => {
    const qUsers = query(collection(db, 'users'));
    const unsubscribeUsers = onSnapshot(qUsers, (snapshot) => {
      const fetchedUsers: User[] = snapshot.docs.map(d => {
        const data = d.data();
        return {
          id: d.id,
          ...data,
          activities: data.activities || []
        } as User;
      });
      setUsers(fetchedUsers);
      setUsersLoaded(true);
    }, (error) => {
      console.error("Error fetching users:", error);
    });

    return () => unsubscribeUsers();
  }, []);

  // Teams Listener
  useEffect(() => {
    const qTeams = query(collection(db, 'teams'));
    const unsubscribeTeams = onSnapshot(qTeams, (snapshot) => {
      const fetchedTeams: Team[] = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as Team));
      setTeams(fetchedTeams);
    }, (error) => {
      console.error("Error fetching teams:", error);
    });

    return () => unsubscribeTeams();
  }, []);

  // Fetch System Settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settings = await getSystemSettings();
        setSystemSettings(settings);
      } catch (error) {
        console.error("Error fetching system settings:", error);
      }
    };
    fetchSettings();
  }, []);

  return { users, teams, systemSettings, usersLoaded, setUsers, setSystemSettings };
};
